import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';

import type { KBTagDefinition } from '@/common-types';
import { kbTagService } from '@/services/api';
import { KbHistoryPanel } from './kb-history-panel';
import { FetchError } from '@/common-utilities/fetch-utilities';

function formatTs(ts?: string): string {
  if (!ts) return '—';
  const d = dayjs(ts);
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : ts;
}

function parseNestedErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Request failed';
  const d = data as Record<string, unknown>;
  if (typeof d.message === 'string' && d.message) return d.message;
  const err = d.error;
  if (typeof err === 'string' && err) return err;
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return 'Request failed';
}

function linkedCountFrom409(data: unknown): number | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const err = (data as { error?: unknown }).error;
  if (!err || typeof err !== 'object') return undefined;
  const n = (err as { linkedArticleCount?: unknown }).linkedArticleCount;
  return typeof n === 'number' ? n : undefined;
}

export interface KbTagManagerDialogProps {
  open: boolean;
  onClose: () => void;
  tags: KBTagDefinition[];
  loading: boolean;
  onTagsChanged: () => void | Promise<void>;
}

export function KbTagManagerDialog({
  open,
  onClose,
  tags,
  loading,
  onTagsChanged,
}: KbTagManagerDialogProps) {
  const [showAddRow, setShowAddRow] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [editing, setEditing] = useState<KBTagDefinition | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteState, setDeleteState] = useState<
    | null
    | { phase: 'confirm'; tag: KBTagDefinition }
    | { phase: 'force'; tag: KBTagDefinition; linkedArticleCount: number }
  >(null);
  const [deleting, setDeleting] = useState(false);

  const [dialogError, setDialogError] = useState<string | null>(null);
  const [tagHistoryFor, setTagHistoryFor] = useState<KBTagDefinition | null>(null);

  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.key.localeCompare(b.key)),
    [tags],
  );

  const resetAddForm = () => {
    setNewKey('');
    setNewDescription('');
    setShowAddRow(false);
  };

  const handleClose = () => {
    if (creating || savingEdit || deleting) return;
    resetAddForm();
    setEditing(null);
    setDeleteState(null);
    setDialogError(null);
    setTagHistoryFor(null);
    onClose();
  };

  const handleCreate = async () => {
    const key = newKey.trim();
    if (!key) {
      setDialogError('Tag key is required');
      return;
    }
    setCreating(true);
    setDialogError(null);
    try {
      await kbTagService.createTagDefinition({
        key,
        description: newDescription.trim() || undefined,
      });
      resetAddForm();
      await onTagsChanged();
    } catch (e) {
      setDialogError(
        e instanceof FetchError ? parseNestedErrorMessage(e.data) : e instanceof Error ? e.message : 'Failed to create tag',
      );
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (tag: KBTagDefinition) => {
    setEditing(tag);
    setEditDescription(tag.description || '');
    setDialogError(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    setDialogError(null);
    try {
      await kbTagService.updateTagDefinition(editing.tagId, {
        description: editDescription.trim(),
      });
      setEditing(null);
      await onTagsChanged();
    } catch (e) {
      setDialogError(
        e instanceof FetchError ? parseNestedErrorMessage(e.data) : e instanceof Error ? e.message : 'Failed to update tag',
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const tryDelete = async (tag: KBTagDefinition, force: boolean) => {
    setDeleting(true);
    setDialogError(null);
    try {
      await kbTagService.deleteTagDefinition(tag.tagId, force ? { force: true } : undefined);
      setDeleteState(null);
      await onTagsChanged();
    } catch (e) {
      if (e instanceof FetchError && e.status === 409 && !force) {
        const count = linkedCountFrom409(e.data);
        if (count !== undefined) {
          setDeleteState({ phase: 'force', tag, linkedArticleCount: count });
        } else {
          setDialogError(parseNestedErrorMessage(e.data));
        }
      } else {
        setDialogError(
          e instanceof FetchError ? parseNestedErrorMessage(e.data) : e instanceof Error ? e.message : 'Failed to delete tag',
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <TagIcon color="primary" />
          Manage tag keys
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
            ({tags.length} registered)
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={creating || savingEdit || deleting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tag keys must exist here before they can be used on articles. The key is permanent; only the description can be edited later.
          </Typography>

          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2">Registry</Typography>
            {!showAddRow && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => {
                  setShowAddRow(true);
                  setDialogError(null);
                }}
                disabled={loading || creating}
              >
                Add tag key
              </Button>
            )}
          </Stack>

          {showAddRow && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              alignItems={{ sm: 'flex-start' }}
              sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <TextField
                size="small"
                label="Key"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="e.g. Type"
                required
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Shown to editors when tagging"
                sx={{ flex: 2, minWidth: 200 }}
              />
              <Stack direction="row" gap={0.5}>
                <Button size="small" variant="contained" onClick={() => void handleCreate()} disabled={creating || !newKey.trim()}>
                  {creating ? 'Saving…' : 'Save'}
                </Button>
                <Button size="small" onClick={resetAddForm} disabled={creating}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}

          <TableContainer>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Created</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Updated</TableCell>
                  <TableCell align="right" width={120}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton width={100} /></TableCell>
                      <TableCell><Skeleton width={200} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                    </TableRow>
                  ))
                ) : sortedTags.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        No tag keys yet. Use &quot;Add tag key&quot; to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedTags.map((tag) => (
                    <TableRow key={tag.tagId} hover>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {tag.key}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', maxWidth: 280 }}>
                        <Typography variant="body2" noWrap title={tag.description}>
                          {tag.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {formatTs(tag.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {formatTs(tag.updatedAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Change history">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setTagHistoryFor(tag);
                              setDialogError(null);
                            }}
                            aria-label={`History for ${tag.key}`}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit description">
                          <IconButton size="small" onClick={() => openEdit(tag)} aria-label={`Edit ${tag.key}`}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete tag definition">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteState({ phase: 'confirm', tag });
                              setDialogError(null);
                            }}
                            aria-label={`Delete ${tag.key}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={creating || savingEdit || deleting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit description */}
      <Dialog open={!!editing} onClose={() => !savingEdit && setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit tag</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Key <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{editing?.key}</Box> cannot be changed.
          </Typography>
          {editing && (
            <Stack spacing={0.25} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {formatTs(editing.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Last updated: {formatTs(editing.updatedAt)}
              </Typography>
            </Stack>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            sx={{ mt: 1 }}
          />
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogError(null);
              setEditing(null);
            }}
            disabled={savingEdit}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSaveEdit()} disabled={savingEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag registry change history */}
      <Dialog
        open={!!tagHistoryFor}
        onClose={() => setTagHistoryFor(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <HistoryIcon color="action" fontSize="small" />
          Tag change history
          {tagHistoryFor && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              (
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {tagHistoryFor.key}
              </Box>
              )
            </Typography>
          )}
          <IconButton
            aria-label="Close"
            onClick={() => setTagHistoryFor(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Creates, description updates, and deletions for this tag definition (DynamoDB audit trail).
          </Typography>
          <KbHistoryPanel
            entries={tagHistoryFor?.history}
            emptyMessage="No history on this tag yet (older definitions may predate history tracking)."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTagHistoryFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteState}
        onClose={() => !deleting && setDeleteState(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete tag key?</DialogTitle>
        <DialogContent>
          {deleteState?.phase === 'confirm' && (
            <Typography variant="body2">
              Remove the tag definition <strong>{deleteState.tag.key}</strong>? Articles will not be deleted; if any article still uses this key, you will be asked to confirm a forced removal.
            </Typography>
          )}
          {deleteState?.phase === 'force' && (
            <Alert severity="warning" sx={{ mb: 0 }}>
              {deleteState.linkedArticleCount} article(s) still use &quot;{deleteState.tag.key}&quot;. Deleting will remove this tag from all of those articles, then delete the definition. This cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteState(null)} disabled={deleting}>Cancel</Button>
          {deleteState?.phase === 'confirm' && (
            <Button
              variant="contained"
              color="error"
              onClick={() => void tryDelete(deleteState.tag, false)}
              disabled={deleting}
            >
              {deleting ? '…' : 'Delete'}
            </Button>
          )}
          {deleteState?.phase === 'force' && (
            <Button
              variant="contained"
              color="error"
              onClick={() => void tryDelete(deleteState.tag, true)}
              disabled={deleting}
            >
              {deleting ? '…' : 'Remove from articles and delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default KbTagManagerDialog;
